import { resolveOfferedOptionValues } from './shared-responder-v2-core-option.helpers';
import { createThreadBehaviour } from './shared-responder-v2-core-thread-behaviour';
import type { ResponderDraftEvent } from './shared-responder-v2-core-thread-behaviour';
import { THREAD_KINDS } from './shared-responder-v2-core-thread.consts';
import type {
  QuestionSpec,
  ResponderAnswer,
} from './shared-responder-v2-core-thread.types';

type RankingDraft = Extract<ResponderAnswer, { kind: 'ranking' }>;

export function emptyRankingDraft(spec: QuestionSpec): RankingDraft {
  return { kind: 'ranking', orderedOptionValues: spec.optionValues };
}

/** A permutation check by multiset equality, so a dropped or duplicated option is rejected. */
function isPermutation(
  candidate: readonly string[],
  reference: readonly string[]
): boolean {
  if (candidate.length !== reference.length) {
    return false;
  }

  const remainingCounts = new Map<string, number>();
  for (const value of reference) {
    remainingCounts.set(value, (remainingCounts.get(value) ?? 0) + 1);
  }

  for (const value of candidate) {
    const remaining = remainingCounts.get(value);
    if (remaining === undefined || remaining === 0) {
      return false;
    }
    remainingCounts.set(value, remaining - 1);
  }

  return true;
}

function resolveSpokenRankingOrder(
  spokenValues: readonly string[],
  spec: QuestionSpec
): readonly string[] | null {
  const spoken = resolveOfferedOptionValues(spokenValues, spec);

  return spoken.length === 0
    ? null
    : [
        ...spoken,
        ...spec.optionValues.filter((value) => !spoken.includes(value)),
      ];
}

export function reduceRankingDraft(
  draft: ResponderAnswer | null,
  event: ResponderDraftEvent,
  spec: QuestionSpec
): ResponderAnswer | null {
  if (event.type === 'spokenPick') {
    const orderedOptionValues = resolveSpokenRankingOrder(
      event.optionValues,
      spec
    );

    return orderedOptionValues === null
      ? draft
      : { kind: 'ranking', orderedOptionValues };
  }

  if (event.type !== 'rankingReordered') {
    return draft;
  }

  if (!isPermutation(event.orderedOptionValues, spec.optionValues)) {
    return draft;
  }

  return { kind: 'ranking', orderedOptionValues: event.orderedOptionValues };
}

export function isRankingDraftComplete(draft: ResponderAnswer | null): boolean {
  return draft !== null;
}

export const RANKING_THREAD_BEHAVIOUR = createThreadBehaviour({
  kind: THREAD_KINDS.Ranking,
  spokenAffordances: { extractsSpokenPick: true },
  emptyDraft: emptyRankingDraft,
  reduceDraft: reduceRankingDraft,
  isDraftComplete: isRankingDraftComplete,
  // A ranking has no discrete "pick" moment — the order is meaningful from the
  // first render, so the responder must always be able to confirm what they see.
  showsContinueButton: () => true,
});
